import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { stateCoordinates } from "@/lib/stateCoordinates";
import { cityMatches } from "@/lib/mapNav";
import type { Hospital } from "@/types/map";

interface UseCesiumViewerOptions {
  brand: any;
  cesiumContainerRef: React.MutableRefObject<HTMLDivElement | null>;
}

export function useCesiumViewer({ brand, cesiumContainerRef }: UseCesiumViewerOptions) {
  const { toast } = useToast();
  const viewerRef = useRef<any>(null);
  const orbitListenerRef = useRef<any>(null);
  const isOrbitingRef = useRef(false);
  const autoTourIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [cameraDebugInfo, setCameraDebugInfo] = useState<any>(null);
  const [savedCameraSettings, setSavedCameraSettings] = useState<any>(null);
  const [isAutoOrbiting, setIsAutoOrbiting] = useState(false);
  const [isContinuousOrbit, setIsContinuousOrbit] = useState(false);
  const [isAutoTour, setIsAutoTour] = useState(false);
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);

  const isAhp = brand.key === 'ahp';

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, [inactivityTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoTourIntervalRef.current) clearInterval(autoTourIntervalRef.current);
      stopOrbit();
      if (viewerRef.current && (viewerRef.current as any)._keyboardCleanup) {
        (viewerRef.current as any)._keyboardCleanup();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeCesium = async () => {
    if (mapLoaded || !cesiumContainerRef.current) return;

    try {
      const cssLink = document.createElement('link');
      cssLink.href = 'https://ajax.googleapis.com/ajax/libs/cesiumjs/1.105/Build/Cesium/Widgets/widgets.css';
      cssLink.rel = 'stylesheet';
      document.head.appendChild(cssLink);

      const script = document.createElement('script');
      script.src = 'https://ajax.googleapis.com/ajax/libs/cesiumjs/1.105/Build/Cesium/Cesium.js';

      script.onload = async () => {
        const Cesium = (window as any).Cesium;
        Cesium.Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIwNjRhYWMxZS1iOTlmLTQ2MGMtYmQyZC1iMWY1NzA3NTczM2IiLCJpZCI6MjQ1NjY2LCJpYXQiOjE3Mjc5NDMyMDN9.ee1aX67GKnALEv83uRNYgdLnxGWMkUrhvXuL0rZd48c';

        const viewer = new Cesium.Viewer('cesiumContainer', {
          imageryProvider: false,
          baseLayerPicker: false,
          requestRenderMode: true,
          timeline: false,
          animation: false,
          homeButton: false,
          fullscreenButton: false,
          vrButton: false,
          sceneModePicker: false,
          navigationHelpButton: false,
          geocoder: false,
        });

        // PERF 4c: Camera change threshold
        viewer.camera.percentageChanged = 0.02;

        // Load skybox
        const loadSkybox = async () => {
          if (brand.key === 'antigreed' || brand.key === 'ahp') return "cream-solid";
          try {
            const { data: settings, error } = await supabase
              .from("system_settings")
              .select("setting_value")
              .eq("setting_key", "active_skybox")
              .single();
            if (error) return "minimal-stars";
            return settings?.setting_value || "minimal-stars";
          } catch { return "minimal-stars"; }
        };

        const activeSkybox = await loadSkybox();
        const skyboxPath = `/skybox/${activeSkybox}`;

        viewer.scene.skyBox = new Cesium.SkyBox({
          sources: {
            positiveX: `${skyboxPath}/cube_right.png`,
            negativeX: `${skyboxPath}/cube_left.png`,
            positiveY: `${skyboxPath}/cube_up.png`,
            negativeY: `${skyboxPath}/cube_down.png`,
            positiveZ: `${skyboxPath}/cube_back.png`,
            negativeZ: `${skyboxPath}/cube_front.png`
          }
        });

        // PERF 4a: Tileset LOD tuning
        const tileset = viewer.scene.primitives.add(new Cesium.Cesium3DTileset({
          url: "https://tile.googleapis.com/v1/3dtiles/root.json?key=AIzaSyC_5B-2VQwssUnAloHSvBGdimTvwp51W0c",
          showCreditsOnScreen: true,
          maximumScreenSpaceError: 48,
          maximumMemoryUsage: 512,
          skipLevelOfDetail: true,
          preferLeaves: true,
          immediatelyLoadDesiredLevelOfDetail: true,
        }));
        tileset.depthTestAgainstTerrain = true;

        // PERF 4b: Scene optimizations
        viewer.scene.fog.enabled = true;
        viewer.scene.fog.density = 2.0e-4;
        viewer.scene.fog.screenSpaceErrorFactor = 2.0;
        viewer.scene.logarithmicDepthBuffer = true;
        viewer.scene.globe.enableLighting = false;
        viewer.shadows = false;
        viewer.scene.highDynamicRange = false;
        viewer.scene.postProcessStages.fxaa.enabled = false;

        // Add US state borders classified onto 3D tiles (works with globe.show=false)
        fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json')
          .then((r: any) => r.json())
          .then((geojson: any) => {
            const geometryInstances: any[] = [];

            geojson.features.forEach((feature: any) => {
              const geom = feature.geometry;
              const polygons = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;

              polygons.forEach((polygon: any) => {
                const ring = polygon[0];
                // Flatten [lon, lat] pairs into [lon, lat, lon, lat, ...]
                const degreesArray: number[] = [];
                ring.forEach(([lon, lat]: [number, number]) => {
                  degreesArray.push(lon, lat);
                });

                if (degreesArray.length >= 4) {
                  geometryInstances.push(new Cesium.GeometryInstance({
                    geometry: new Cesium.GroundPolylineGeometry({
                      positions: Cesium.Cartesian3.fromDegreesArray(degreesArray),
                      width: 3.0,
                    }),
                    attributes: {
                      color: Cesium.ColorGeometryInstanceAttribute.fromColor(
                        Cesium.Color.BLACK.withAlpha(0.9)
                      ),
                    },
                  }));
                }
              });
            });

            if (geometryInstances.length > 0) {
              const borderPrimitive = new Cesium.GroundPolylinePrimitive({
                geometryInstances: geometryInstances,
                appearance: new Cesium.PolylineMaterialAppearance({
                  material: Cesium.Material.fromType('Color', {
                    color: Cesium.Color.BLACK.withAlpha(0.9),
                  }),
                }),
                classificationType: Cesium.ClassificationType.CESIUM_3D_TILE,
              });

              viewer.scene.groundPrimitives.add(borderPrimitive);
              (viewer as any)._borderPrimitive = borderPrimitive;
              viewer.scene.requestRender();
              if (import.meta.env.DEV) console.log(`✓ Loaded ${geometryInstances.length} state border segments`);
            }
          })
          .catch((err: any) => console.warn('Failed to load state borders:', err));

        // Terrain constraints
        viewer.scene.screenSpaceCameraController.minimumZoomDistance = 500;
        viewer.scene.screenSpaceCameraController.enableCollisionDetection = true;

        const minimumHeightAboveTerrain = 500;

        // Camera height enforcement
        viewer.scene.camera.moveEnd.addEventListener(() => {
          const position = viewer.scene.camera.positionCartographic;
          if (position.height < minimumHeightAboveTerrain) {
            viewer.scene.camera.position = Cesium.Cartesian3.fromRadians(
              position.longitude, position.latitude, minimumHeightAboveTerrain
            );
          }
        });

        viewer.scene.camera.changed.addEventListener(() => {
          const position = viewer.scene.camera.positionCartographic;
          if (position.height < minimumHeightAboveTerrain) {
            viewer.scene.camera.position = Cesium.Cartesian3.fromRadians(
              position.longitude, position.latitude, minimumHeightAboveTerrain
            );
          }
        });

        // Post-render height check - throttled
        let lastHeightCheckTime = 0;
        viewer.scene.postRender.addEventListener(() => {
          const now = performance.now();
          if (now - lastHeightCheckTime < 200) return;
          lastHeightCheckTime = now;
          if (viewer.scene.camera) {
            const position = viewer.scene.camera.positionCartographic;
            if (position.height < minimumHeightAboveTerrain) {
              viewer.scene.camera.position = Cesium.Cartesian3.fromRadians(
                position.longitude, position.latitude, minimumHeightAboveTerrain
              );
            }
          }
        });

        // Pitch safeguard
        const originalLookAt = viewer.scene.camera.lookAt.bind(viewer.scene.camera);
        viewer.scene.camera.lookAt = function(...args: any[]) {
          const result = originalLookAt(...args);
          const position = viewer.scene.camera.positionCartographic;
          if (position.height < 1000) {
            const currentPitch = Cesium.Math.toDegrees(viewer.scene.camera.pitch);
            if (currentPitch < -85) {
              viewer.scene.camera.setView({
                orientation: {
                  heading: viewer.scene.camera.heading,
                  pitch: Cesium.Math.toRadians(-85),
                  roll: viewer.scene.camera.roll
                }
              });
            }
          }
          return result;
        };

        // Controller tuning
        const controller = viewer.scene.screenSpaceCameraController;
        controller.zoomEventTypes = [Cesium.CameraEventType.WHEEL, Cesium.CameraEventType.PINCH];
        controller.minimumPickingTerrainHeight = minimumHeightAboveTerrain;
        controller.minimumCollisionTerrainHeight = minimumHeightAboveTerrain;
        controller.minimumTrackBallHeight = minimumHeightAboveTerrain;
        controller.inertiaSpin = 0.5;
        controller.inertiaTranslate = 0.5;
        controller.inertiaZoom = 0.5;

        viewer.scene.globe.show = false;

        // Set initial camera position
        const startPosition = {
          longitude: -77.76546819391845, latitude: 31.923773347582962,
          height: 910041.2970978987, heading: 279.053781771115,
          pitch: -29.977831357899564, roll: 0.00
        };

        viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(startPosition.longitude, startPosition.latitude, startPosition.height),
          orientation: {
            heading: Cesium.Math.toRadians(startPosition.heading),
            pitch: Cesium.Math.toRadians(startPosition.pitch),
            roll: startPosition.roll
          }
        });

        // Interaction handlers to stop orbit
        let isMouseDown = false;
        let isDragging = false;
        let mouseStartPosition = { x: 0, y: 0 };

        const stopOrbitOnInteraction = () => {
          stopOrbit();
          setIsAutoOrbiting(false);
          resetInactivityTimer();
        };

        const handleMouseDown = (e: MouseEvent) => {
          isMouseDown = true; isDragging = false;
          mouseStartPosition = { x: e.clientX, y: e.clientY };
          stopOrbitOnInteraction();
        };

        const handleMouseMove = (e: MouseEvent) => {
          if (isMouseDown) {
            const dx = e.clientX - mouseStartPosition.x;
            const dy = e.clientY - mouseStartPosition.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
              isDragging = true;
              stopOrbitOnInteraction();
            }
          }
          resetInactivityTimer();
        };

        const handleMouseUp = () => {
          if (isDragging) stopOrbitOnInteraction();
          isMouseDown = false; isDragging = false;
        };

        viewer.camera.moveStart.addEventListener(stopOrbitOnInteraction);
        viewer.scene.canvas.addEventListener('mousedown', handleMouseDown);
        viewer.scene.canvas.addEventListener('mousemove', handleMouseMove);
        viewer.scene.canvas.addEventListener('mouseup', handleMouseUp);
        viewer.scene.canvas.addEventListener('wheel', stopOrbitOnInteraction);
        viewer.scene.canvas.addEventListener('touchstart', stopOrbitOnInteraction);
        viewer.scene.canvas.addEventListener('keydown', stopOrbitOnInteraction);

        // Keyboard flight controls
        const keyboardControls = new Set();
        let animationFrameId: number | null = null;

        const handleKeyDown = (event: KeyboardEvent) => {
          const target = event.target as HTMLElement;
          const isInputField = target && (
            target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
            target.contentEditable === 'true' || target.closest('input') || target.closest('textarea')
          );
          if (isInputField) return;
          stopOrbitOnInteraction();

          const key = event.key.toLowerCase();
          if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'pageup', 'pagedown'].includes(key)) {
            event.preventDefault();
            keyboardControls.add(key);
            if (!animationFrameId) {
              const moveCamera = () => {
                if (!viewer || !viewer.camera || keyboardControls.size === 0) {
                  animationFrameId = null; return;
                }
                const camera = viewer.camera;
                const moveAmount = 5000;
                const elevationAmount = 50;
                if (keyboardControls.has('w') || keyboardControls.has('arrowup')) camera.moveForward(moveAmount);
                if (keyboardControls.has('s') || keyboardControls.has('arrowdown')) camera.moveBackward(moveAmount);
                if (keyboardControls.has('a') || keyboardControls.has('arrowleft')) camera.moveLeft(moveAmount);
                if (keyboardControls.has('d') || keyboardControls.has('arrowright')) camera.moveRight(moveAmount);
                if (keyboardControls.has('pageup')) camera.moveUp(elevationAmount);
                if (keyboardControls.has('pagedown')) camera.moveDown(elevationAmount);
                animationFrameId = requestAnimationFrame(moveCamera);
              };
              animationFrameId = requestAnimationFrame(moveCamera);
            }
          }
        };

        const handleKeyUp = (event: KeyboardEvent) => {
          keyboardControls.delete(event.key.toLowerCase());
          if (keyboardControls.size === 0 && animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
          }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);

        (viewer as any)._keyboardCleanup = () => {
          document.removeEventListener('keydown', handleKeyDown);
          document.removeEventListener('keyup', handleKeyUp);
          viewer.scene.canvas.removeEventListener('mousedown', handleMouseDown);
          viewer.scene.canvas.removeEventListener('mousemove', handleMouseMove);
          viewer.scene.canvas.removeEventListener('mouseup', handleMouseUp);
          if (animationFrameId) cancelAnimationFrame(animationFrameId);
        };

        viewerRef.current = viewer;
        (window as any).cesiumViewer = viewer;

        (window as any).exportCameraPosition = () => {
          if (!viewer || !viewer.camera) return null;
          const camera = viewer.camera;
          const cartographic = camera.positionCartographic;
          const cameraSettings = {
            longitude: Cesium.Math.toDegrees(cartographic.longitude),
            latitude: Cesium.Math.toDegrees(cartographic.latitude),
            height: cartographic.height,
            heading: Cesium.Math.toDegrees(camera.heading),
            pitch: Cesium.Math.toDegrees(camera.pitch),
            roll: Cesium.Math.toDegrees(camera.roll)
          };
          if (import.meta.env.DEV) console.log('📍 Current Camera Position:', cameraSettings);
          return cameraSettings;
        };

        setMapLoaded(true);
      };

      document.head.appendChild(script);
    } catch (error) {
      console.error("Error loading Cesium:", error);
    }
  };

  const stopOrbit = () => {
    if (orbitListenerRef.current) {
      orbitListenerRef.current();
      orbitListenerRef.current = null;
    }
    setIsAutoOrbiting(false);
    setIsContinuousOrbit(false);
    isOrbitingRef.current = false;

    if (viewerRef.current) {
      const Cesium = (window as any).Cesium;
      if (Cesium) {
        viewerRef.current.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
      }
    }
  };

  const resetInactivityTimer = () => {
    // This is a no-op stub; the actual inactivity timer logic is managed
    // by the parent component since it needs selectedHospital state
  };

  const orbitHospital = (hospital: Hospital) => {
    if (!viewerRef.current) return;
    const Cesium = (window as any).Cesium;
    if (!Cesium) return;

    stopOrbit();

    const longitude = Number(hospital.longitude);
    const latitude = Number(hospital.latitude);
    const groundElevation = (hospital as any).elevation || 0;

    if (!isNaN(longitude) && !isNaN(latitude)) {
      viewerRef.current.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(longitude, latitude, groundElevation + 400),
        orientation: {
          heading: Cesium.Math.toRadians(87.61),
          pitch: Cesium.Math.toRadians(-24.86),
          roll: 0.0
        },
        duration: 3.0,
        complete: () => {
          startCinematicOrbit(hospital, {
            distance: 450,
            heading: Cesium.Math.toRadians(87.61),
            pitch: -24.86,
            height: groundElevation + 10
          });
        }
      });
    } else {
      toast({
        title: "Error",
        description: `Invalid coordinates for ${hospital.name}`,
        variant: "destructive",
      });
    }
  };

  // PERF 4d: Fix orbit listener leak — proper cleanup via removeEventListener
  const startCinematicOrbit = (hospital: Hospital, options: { distance: number; heading: number; pitch: number; height: number }) => {
    if (!viewerRef.current) return;
    const Cesium = (window as any).Cesium;
    if (!Cesium) return;

    stopOrbit();

    const target = Cesium.Cartesian3.fromDegrees(hospital.longitude, hospital.latitude, options.height);
    let startTime = performance.now();
    isOrbitingRef.current = true;

    const orbitListener = () => {
      if (!isOrbitingRef.current || !viewerRef.current) return;
      const currentTime = performance.now();
      const elapsed = (currentTime - startTime) / 1000;
      const revolutionTime = 90;
      const angle = (elapsed / revolutionTime) * 2 * Math.PI;

      const headingPitchRange = new Cesium.HeadingPitchRange(
        options.heading + angle,
        Cesium.Math.toRadians(options.pitch),
        options.distance
      );
      viewerRef.current.camera.lookAt(target, headingPitchRange);
    };

    // Store cleanup that calls removeEventListener (not just the listener ref)
    viewerRef.current.clock.onTick.addEventListener(orbitListener);
    orbitListenerRef.current = () => {
      if (viewerRef.current) {
        viewerRef.current.clock.onTick.removeEventListener(orbitListener);
      }
    };
  };

  const startContinuousRotation = (position?: any, orientation?: any) => {
    if (!viewerRef.current) return;
    const Cesium = (window as any).Cesium;
    if (!Cesium) return;

    setIsContinuousOrbit(true);
    isOrbitingRef.current = true;

    const camera = viewerRef.current.camera;
    const initialPosition = position || camera.position.clone();
    const initialPitch = orientation?.pitch || camera.pitch;
    const initialRoll = orientation?.roll || camera.roll;
    const startingHeading = orientation?.heading || camera.heading;
    let startTime = performance.now();
    let animationId: number;

    const continuousRotationLoop = () => {
      if (!isOrbitingRef.current || !viewerRef.current) return;
      const currentTime = performance.now();
      const elapsed = (currentTime - startTime) / 1000;
      const rotationTime = 120;
      const rotationAngle = (elapsed / rotationTime) * 2 * Math.PI;
      const currentHeading = startingHeading + rotationAngle;

      viewerRef.current.camera.setView({
        destination: initialPosition,
        orientation: { heading: currentHeading, pitch: initialPitch, roll: initialRoll }
      });

      animationId = requestAnimationFrame(continuousRotationLoop);
    };

    animationId = requestAnimationFrame(continuousRotationLoop);
    orbitListenerRef.current = () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  };

  // State coordinates imported from shared module (see top of file)

  const flyToState = (stateCode: string) => {
    if (!viewerRef.current) return;
    const Cesium = (window as any).Cesium;
    if (!Cesium) return;

    const coords = stateCoordinates[stateCode];
    if (!coords) return;

    stopOrbit();

    const orientation = stateCode === 'USA'
      ? {
          heading: Cesium.Math.toRadians(16.28),
          pitch: Cesium.Math.toRadians(-86.65),
          roll: Cesium.Math.toRadians(0.41)
        }
      : {
          heading: Cesium.Math.toRadians(0),
          pitch: Cesium.Math.toRadians(-90),
          roll: 0.0
        };

    viewerRef.current.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(coords.longitude, coords.latitude, coords.height),
      orientation,
      duration: 2.0
    });
  };

  const flyToCity = (city: string, stateCode: string, hospitals: Hospital[] = []) => {
    if (!viewerRef.current) return;
    const Cesium = (window as any).Cesium;
    if (!Cesium) return;

    const cityHospitals = hospitals.filter(h =>
      cityMatches(h.city, city) &&
      h.state.toUpperCase() === stateCode.toUpperCase() &&
      h.latitude && h.longitude
    );

    if (cityHospitals.length === 0) {
      flyToState(stateCode);
      return;
    }

    const avgLatitude = cityHospitals.reduce((sum, h) => sum + (h.latitude || 0), 0) / cityHospitals.length;
    const avgLongitude = cityHospitals.reduce((sum, h) => sum + (h.longitude || 0), 0) / cityHospitals.length;

    stopOrbit();

    viewerRef.current.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(avgLongitude, avgLatitude, 100000),
      orientation: { heading: Cesium.Math.toRadians(0), pitch: Cesium.Math.toRadians(-90), roll: 0.0 },
      duration: 2.0
    });
  };

  const loadCameraSettings = async (hospitalId: string) => {
    try {
      const { data, error } = await supabase
        .from("hospital_camera_settings")
        .select("*")
        .eq("hospital_id", hospitalId)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      console.error("Error loading camera settings:", error);
      return null;
    }
  };

  const saveCameraSettings = async (selectedHospital: Hospital | null) => {
    if (!selectedHospital || !cameraDebugInfo) {
      toast({ title: "Cannot Save", description: "No hospital selected or camera data available", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase
        .from("hospital_camera_settings")
        .upsert({
          hospital_id: selectedHospital.id,
          longitude: parseFloat(cameraDebugInfo.longitude),
          latitude: parseFloat(cameraDebugInfo.latitude),
          height: parseFloat(cameraDebugInfo.height),
          heading: parseFloat(cameraDebugInfo.heading),
          pitch: parseFloat(cameraDebugInfo.pitch),
          roll: parseFloat(cameraDebugInfo.roll),
        });
      if (error) throw error;
      toast({ title: "Camera Settings Saved", description: `Optimal view saved for ${selectedHospital.name}` });
      const settings = await loadCameraSettings(selectedHospital.id);
      setSavedCameraSettings(settings);
    } catch (error) {
      console.error("Error saving camera settings:", error);
      toast({ title: "Save Failed", description: "Failed to save camera settings", variant: "destructive" });
    }
  };

  const startAutoTour = (hospitals: Hospital[], selectedHospital: Hospital | null, setSelectedHospital: (h: Hospital) => void, fetchHospitalProcedures: (id: string) => void) => {
    const followedHospitals = hospitals.filter(h => h.followed);
    if (followedHospitals.length === 0) return;
    setIsAutoTour(true);
    const currentIndex = selectedHospital
      ? followedHospitals.findIndex(h => h.id === selectedHospital.id) : 0;
    const hospital = followedHospitals[currentIndex];
    setSelectedHospital(hospital);
    fetchHospitalProcedures(hospital.id);
    orbitHospital(hospital);
  };

  const stopAutoTour = () => {
    setIsAutoTour(false);
    setIsAutoOrbiting(false);
    setIsContinuousOrbit(false);
    if (autoTourIntervalRef.current) {
      clearInterval(autoTourIntervalRef.current);
      autoTourIntervalRef.current = null;
    }
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      setInactivityTimer(null);
    }
    stopOrbit();
  };

  return {
    viewerRef,
    mapLoaded,
    isAutoOrbiting,
    setIsAutoOrbiting,
    isContinuousOrbit,
    setIsContinuousOrbit,
    isOrbitingRef,
    inactivityTimer,
    setInactivityTimer,
    isAutoTour,
    cameraDebugInfo,
    savedCameraSettings,
    autoTourIntervalRef,
    initializeCesium,
    stopOrbit,
    orbitHospital,
    startCinematicOrbit,
    startContinuousRotation,
    flyToState,
    flyToCity,
    loadCameraSettings,
    saveCameraSettings,
    startAutoTour,
    stopAutoTour,
  };
}
